import React,{useCallback,useEffect,useMemo,useState}from"react";
import{useTranslation}from"react-i18next";
import{fleetOpsApi as A}from"../api/fleetOpsApi";
import s from"./FleetOps.module.css";

const EMPTY={supplier:"",source_folder:"",status:"",date_from:"",date_to:""};
const EMPTY_STATS={count:0,records:0,subtotal:0,vat:0,total:0,review:0,possible_duplicates:0,auto_review:0};

function apiErrorMessage(error,fallback){
  const data=error?.response?.data;
  const detail=data?.detail??data?.message??error?.message;
  if(typeof detail==="string"&&detail.trim())return detail;
  if(Array.isArray(detail)){
    const parts=detail.map(item=>{
      if(typeof item==="string")return item;
      if(item&&typeof item==="object"){
        const where=Array.isArray(item.loc)?item.loc.filter(x=>x!=="query"&&x!=="body").join(" → "):"";
        const msg=typeof item.msg==="string"?item.msg:"";
        return [where,msg].filter(Boolean).join(": ");
      }
      return "";
    }).filter(Boolean);
    if(parts.length)return parts.join(" · ");
  }
  if(detail&&typeof detail==="object"){
    if(typeof detail.msg==="string")return detail.msg;
    if(typeof detail.message==="string")return detail.message;
  }
  return fallback;
}

export default function InvoiceAnalyticsPage(){
  const{t,i18n}=useTranslation();
  const tr=(key,fallback,opts={})=>{const value=t(key,opts);return typeof value==="string"&&value!==key?value:fallback};
  const[filters,setFilters]=useState(EMPTY),[applied,setApplied]=useState(EMPTY);
  const[stats,setStats]=useState(EMPTY_STATS);
  const[suppliers,setSuppliers]=useState([]),[folders,setFolders]=useState([]),[loading,setLoading]=useState(true),[err,setErr]=useState("");

  // Search endpoint is paginated. Analytics only needs its aggregated stats, but some
  // backend versions validate page_size with a minimum > 1. Use a normal supported
  // page size instead of page_size=1 so a 422 response cannot break this page.
  const params=useMemo(()=>({...Object.fromEntries(Object.entries(applied).filter(([,v])=>v!==""&&v!=null)),page:1,page_size:25}),[applied]);
  const money=v=>new Intl.NumberFormat(i18n.language||"ru",{style:"currency",currency:"EUR",minimumFractionDigits:2}).format(Number(v||0));

  const load=useCallback(async()=>{
    setLoading(true);setErr("");
    try{
      const[searchResult,suppliersResult,foldersResult]=await Promise.allSettled([
        A.searchInvoices(params),
        A.invoiceSuppliers(),
        A.invoiceFolderTree()
      ]);

      if(searchResult.status!=="fulfilled"){
        throw searchResult.reason;
      }

      const nextStats=searchResult.value?.data?.stats||{};
      setStats({...EMPTY_STATS,...nextStats});

      if(suppliersResult.status==="fulfilled"){
        setSuppliers(Array.isArray(suppliersResult.value?.data)?suppliersResult.value.data:[]);
      }else{
        setSuppliers([]);
      }

      if(foldersResult.status==="fulfilled"){
        setFolders(Array.isArray(foldersResult.value?.data?.folders)?foldersResult.value.data.folders:[]);
      }else{
        setFolders([]);
      }
    }catch(e){
      setStats(EMPTY_STATS);
      setErr(apiErrorMessage(e,tr("invoice_analytics_load_error","Не удалось загрузить аналитику")));
    }finally{
      setLoading(false);
    }
  },[params]);

  useEffect(()=>{load()},[load]);
  const apply=()=>setApplied({...filters});
  const clear=()=>{setFilters(EMPTY);setApplied(EMPTY)};
  const processed=Math.max(0,Number(stats.records||0)-Number(stats.review||0));

  return <div className={`${s.page} ${s.invoiceAnalyticsPage}`}>
    <div className={s.header}><div><div className={s.eyebrow}>{tr("invoice_analytics_eyebrow","АНАЛИТИКА ДОКУМЕНТОВ")}</div><h1 className={s.title}>{tr("invoice_analytics_title","Аналитика счетов")}</h1><div className={s.sub}>{tr("invoice_analytics_sub","Финансовые показатели пересчитываются по выбранной компании, проекту, периоду и статусу.")}</div></div><button className={s.btnSecondary} onClick={load}>↻ {tr("refresh","Обновить")}</button></div>
    {err&&<div className={s.alert}>{String(err)}</div>}
    <section className={s.panel}><div className={s.invoiceAnalyticsFilters}>
      <label><span>{tr("invoice_supplier_company","Компания / поставщик")}</span><select className={s.select} value={filters.supplier} onChange={e=>setFilters({...filters,supplier:e.target.value})}><option value="">{tr("invoice_all_companies","Все компании")}</option>{suppliers.map(x=><option key={x.supplier} value={x.supplier}>{x.supplier}</option>)}</select></label>
      <label><span>{tr("invoice_project_folder","Проект / папка")}</span><select className={s.select} value={filters.source_folder} onChange={e=>setFilters({...filters,source_folder:e.target.value})}><option value="">{tr("invoice_all_project_folders","Все проекты / папки")}</option>{folders.filter(x=>x.path).map(x=><option key={x.path} value={x.path}>{`${"— ".repeat(Math.max(0,(x.depth||1)-1))}${x.path}`}</option>)}</select></label>
      <label><span>{tr("invoice_status_filter","Статус")}</span><select className={s.select} value={filters.status} onChange={e=>setFilters({...filters,status:e.target.value})}><option value="">{tr("invoice_status_all","Все статусы")}</option><option value="processed">{tr("invoice_status_processed","Обработан")}</option><option value="review">{tr("invoice_status_needs_review","Проверьте данные")}</option><option value="possible_duplicate">{tr("invoice_status_possible_duplicate","Возможный дубликат")}</option><option value="failed">{tr("invoice_status_failed","Ошибка")}</option></select></label>
      <label><span>{tr("invoice_date_from","Дата от")}</span><input className={s.input} type="date" value={filters.date_from} onChange={e=>setFilters({...filters,date_from:e.target.value})}/></label>
      <label><span>{tr("invoice_date_to","Дата до")}</span><input className={s.input} type="date" value={filters.date_to} onChange={e=>setFilters({...filters,date_to:e.target.value})}/></label>
      <div className={s.invoiceAnalyticsFilterActions}><button className={s.btn} onClick={apply}>{tr("apply_filters","Применить")}</button><button className={s.btnSecondary} onClick={clear}>{tr("reset_filters","Сбросить")}</button></div>
    </div></section>
    {loading?<div className={s.loading}><div className={s.spinner}/>{tr("loading","Загрузка…")}</div>:<>
      <div className={s.invoiceAnalyticsKpis}>
        <div><span>{tr("invoice_documents_count","Документов")}</span><strong>{stats.records||0}</strong><small>{tr("invoice_count","Счетов")}: {stats.count||0}</small></div>
        <div><span>{tr("invoice_net_total","Сумма без НДС")}</span><strong>{money(stats.subtotal)}</strong></div>
        <div><span>{tr("invoice_vat_total","Всего НДС")}</span><strong>{money(stats.vat)}</strong></div>
        <div className={s.invoiceAnalyticsPrimary}><span>{tr("invoice_total","Общая сумма")}</span><strong>{money(stats.total)}</strong></div>
      </div>
      <div className={s.invoiceAnalyticsHealth}>
        <div className={s.invoiceAnalyticsHealthGood}><span>✓</span><div><strong>{processed}</strong><small>{tr("invoice_analytics_processed","Обработано")}</small></div></div>
        <div className={s.invoiceAnalyticsHealthWarn}><span>!</span><div><strong>{stats.review||0}</strong><small>{tr("invoice_analytics_review","Требуют проверки")}</small></div></div>
        <div className={s.invoiceAnalyticsHealthInfo}><span>↻</span><div><strong>{stats.auto_review||0}</strong><small>{tr("invoice_analytics_auto_review","Автоимпорт на проверке")}</small></div></div>
        <div className={s.invoiceAnalyticsHealthDuplicate}><span>⧉</span><div><strong>{stats.possible_duplicates||0}</strong><small>{tr("invoice_analytics_duplicates","Возможные дубликаты")}</small></div></div>
      </div>
      <section className={s.panel}><div className={s.panelHead}><div><h2 className={s.panelTitle}>{tr("invoice_analytics_context_title","Текущая выборка")}</h2><div className={s.panelSub}>{tr("invoice_analytics_context_sub","Все показатели выше относятся только к применённым фильтрам. Для детальной работы с файлами используйте реестр счетов.")}</div></div></div><div className={s.invoiceAnalyticsContext}>{Object.entries(applied).filter(([,v])=>v).length?Object.entries(applied).filter(([,v])=>v).map(([k,v])=><span key={k}>{k}: <strong>{String(v)}</strong></span>):<span>{tr("invoice_analytics_no_filters","Фильтры не применены — показан весь реестр.")}</span>}</div></section>
    </>}
  </div>
}
